// src/components/foam/FoamPrimitives.js
//
// Shared visual primitives for the Foam-direction redesign.
// Doc: /docs/SPEC-2026-05-20-my-waka-redesign.md
//
// Naming convention: prefix `F` for canvas-style primitives.

import React from 'react';

/**
 * WAKA wordmark — official logo PNG (design-system rule: never set text
 * in Fasten outside an actual logo rebuild; use the asset).
 * Assets: /public/assets/waka-logo-black.png · waka-logo-white.png
 */
export const FWordmark = ({ dark = false, size = 15 }) => (
  <img
    src={`${process.env.PUBLIC_URL}/assets/waka-logo-${dark ? 'white' : 'black'}.png`}
    alt="WAKA"
    style={{ height: size, width: 'auto', display: 'block' }}
  />
);

/**
 * Small uppercase mono label — used for section labels, status indicators,
 * step counters, etc. Beach-readability: never below 11px.
 */
export const FLabel = ({ children, color, className = '' }) => (
  <div
    className={['font-mono text-[12px] tracking-[0.14em] uppercase font-medium', className].join(' ')}
    style={{ color: color || 'var(--foam-ink-2)' }}
  >
    {children}
  </div>
);

/**
 * Product-name display text in Audiowide — for board names like
 * "GLIDE 88", "DAY BAG 88", "ROLLING WAKA L" + url lockups ("waka.surf").
 * Design system: Audiowide is `font-model`; `font-display` is Saira Condensed.
 */
export const FBrandText = ({ children, size = 14, color, caps = true, className = '', style = {} }) => (
  <span
    className={['font-model', caps ? 'uppercase' : '', className].join(' ')}
    style={{ fontSize: size, letterSpacing: '0.02em', color, ...style }}
  >
    {children}
  </span>
);

/**
 * Display headline — Saira Condensed 800, UPPERCASE, tight tracking,
 * line-height 0.88–0.92 per the locked design system. The accent word
 * goes in <FHeadline.Accent> (signal orange).
 */
export const FHeadline = ({ children, size = 60, lineHeight = 0.9, color, className = '', style = {} }) => (
  <div
    className={['font-display font-extrabold uppercase', className].join(' ')}
    style={{
      fontSize: size,
      lineHeight,
      letterSpacing: '-0.01em',
      color: color || 'var(--foam-ink)',
      ...style,
    }}
  >
    {children}
  </div>
);

FHeadline.Accent = ({ children, block = false }) => (
  <span style={{ color: 'var(--foam-orange)', display: block ? 'block' : 'inline' }}>
    {children}
  </span>
);

/**
 * Header with WAKA wordmark + optional right-side content.
 * Adds a 1.5px ink (or paper, if dark) horizontal rule beneath.
 */
export const FHeader = ({ dark = false, right = null }) => (
  <div
    className="flex justify-between items-center pb-[14px]"
    style={{
      borderBottom: `1.5px solid ${dark ? 'var(--foam-paper)' : 'var(--foam-line)'}`,
    }}
  >
    <FWordmark dark={dark} />
    {right}
  </div>
);

/**
 * Foam button — filled ink (default), filled paper (dark variant), or
 * filled orange (primary variant). 60px tall, square corners (radius 0
 * per the brutalist-low-radius decision in the SPEC, though we keep a
 * tiny 4px rounding on shipping to soften vs. pure CSS-square).
 */
export const FBtn = ({
  children,
  onClick,
  primary = false,   // accent: orange bg, carbon text
  dark = false,      // on-dark default: paper bg
  ghost = false,     // transparent + 1.5px border (ink on light, paper on dark)
  type = 'button',
  className = '',
  disabled = false,
  height = 60,
}) => {
  const bg = ghost ? 'bg-transparent' : primary ? 'bg-foam-orange' : dark ? 'bg-foam-paper' : 'bg-foam-carbon';
  const text = ghost
    ? (dark ? 'text-foam-paper' : 'text-foam-ink')
    : primary ? 'text-foam-carbon' : dark ? 'text-foam-carbon' : 'text-foam-paper';
  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled}
      className={[
        'w-full flex items-center justify-center gap-[10px]',
        'font-sans font-bold text-[15px] uppercase',
        bg, text,
        disabled ? 'opacity-50 cursor-not-allowed' : 'active:scale-[0.99] transition-transform',
        className,
      ].join(' ')}
      style={{
        height,
        letterSpacing: '0.12em',
        borderRadius: 0, // square everywhere — locked brand decision
        border: ghost ? `1.5px solid ${dark ? 'rgba(255,255,255,0.4)' : 'var(--foam-ink)'}` : 'none',
      }}
    >
      {children}
    </button>
  );
};

/**
 * Board card — the recessed product display in landing + register screens.
 * Renders product image + Audiowide name + mono caption.
 */
export const FBoardCard = ({ product, small = false, dark = false }) => {
  const padding = small ? 14 : 18;
  const imgSize = small ? 70 : 110;
  const nameSize = small ? 24 : 32;
  return (
    <div
      className="flex items-center"
      style={{
        background: dark ? 'var(--foam-carbon)' : 'var(--foam-paper-2)',
        padding,
        gap: small ? 14 : 18,
      }}
    >
      {product?.image && (
        <img
          src={product.image}
          alt=""
          style={{ width: imgSize, height: imgSize, objectFit: 'contain' }}
        />
      )}
      <div className="flex-1 min-w-0">
        <FBrandText
          size={nameSize}
          color={dark ? 'var(--foam-paper)' : 'var(--foam-ink)'}
          style={{ display: 'block', lineHeight: 1 }}
        >
          {product?.name || 'GLIDE 88'}
        </FBrandText>
        <div
          className="font-mono text-[12px] tracking-[0.1em] uppercase mt-[10px]"
          style={{ color: dark ? 'var(--foam-mute)' : 'var(--foam-ink-2)' }}
        >
          {product?.caption || ' '}
        </div>
      </div>
    </div>
  );
};

/**
 * Page-level wrapper — mobile-first iPhone-class layout.
 * Sets the background, padding (56px top / 22px sides / 36px bottom per spec),
 * and a max-width container that centers on tablet/desktop.
 */
export const FPage = ({ dark = false, children, className = '' }) => (
  <div
    className={[
      'min-h-screen w-full mx-auto',
      'max-w-[430px]',
      className,
    ].join(' ')}
    style={{
      background: dark ? 'var(--foam-carbon)' : 'var(--foam-paper)',
      color: dark ? 'var(--foam-paper)' : 'var(--foam-ink)',
      padding: '56px 22px 36px',
      fontFamily: '"Archivo", -apple-system, system-ui, sans-serif',
    }}
  >
    <div className="flex flex-col gap-[22px] min-h-full">
      {children}
    </div>
  </div>
);

/**
 * Pinned-footer screen scaffold — the design-system layout pattern for
 * every screen with a primary CTA. Fixed header, scrollable body, footer
 * pinned to the viewport bottom so the CTA is ALWAYS visible as content
 * grows (more retailers, longer owner messages…).
 *
 * Uses 100dvh (dynamic viewport height) so iOS Safari's collapsing URL
 * bar doesn't hide the footer.
 */
export const FScreen = ({ dark = false, header, footer, children, className = '' }) => (
  <div
    className={['w-full mx-auto max-w-[430px] flex flex-col', className].join(' ')}
    style={{
      height: '100dvh',
      background: dark ? 'var(--foam-carbon)' : 'var(--foam-paper)',
      color: dark ? 'var(--foam-paper)' : 'var(--foam-ink)',
      fontFamily: '"Archivo", -apple-system, system-ui, sans-serif',
    }}
  >
    <div className="flex-shrink-0" style={{ padding: '56px 22px 16px' }}>
      {header}
    </div>
    <div
      className="flex flex-col gap-[18px]"
      style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: '0 22px 14px', WebkitOverflowScrolling: 'touch' }}
    >
      {children}
    </div>
    <div
      className="flex-shrink-0 flex flex-col gap-[10px]"
      style={{
        padding: '14px 22px 30px',
        borderTop: `1px solid ${dark ? 'rgba(255,255,255,0.16)' : 'var(--foam-hair)'}`,
        background: dark ? 'var(--foam-carbon)' : 'var(--foam-paper)',
      }}
    >
      {footer}
    </div>
  </div>
);

/**
 * Apple sign-in glyph (white-on-black variant for filled-ink button).
 */
export const AppleGlyph = ({ size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
    <path d="M17.05 20.28c-.98.95-2.05.94-3.08.47-1.08-.48-2.08-.48-3.21 0-1.39.68-2.12.55-3.18-.47C3.3 16.22 3.78 9.27 8.42 8.9c1.18.04 2.04.46 2.79.52.87-.12 1.71-.56 2.93-.55 1.99.03 3.39.95 4.17 2.48-3.98 2.05-3.28 7.24.74 8.93zm-4.26-14.48c.09-2.57 2.35-4.76 4.76-4.8.26 2.79-2.47 4.88-4.76 4.8z" />
  </svg>
);

/**
 * Google sign-in glyph (multicolor — must show through dark backgrounds).
 */
export const GoogleGlyph = ({ size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24">
    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
  </svg>
);

/**
 * Foam sign-in buttons — Apple + Google stacked, both filled-ink (filled-black).
 * Apple first on iOS / Google first on Android per the SPEC.
 */
export const FSignInButtons = ({ onApple, onGoogle, disabled = false }) => {
  const isIOS = typeof navigator !== 'undefined' &&
    /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
  const order = isIOS ? ['apple', 'google'] : ['google', 'apple'];
  return (
    <div className="flex flex-col gap-[10px]">
      {order.map((p) =>
        p === 'apple' ? (
          <FBtn key="apple" onClick={onApple} disabled={disabled}>
            <AppleGlyph /> Continue with Apple
          </FBtn>
        ) : (
          <FBtn key="google" onClick={onGoogle} disabled={disabled}>
            <GoogleGlyph /> Continue with Google
          </FBtn>
        )
      )}
    </div>
  );
};
